import { type FC, type PropsWithChildren, useMemo, useState } from 'react';

import {
  getNavCategoriesFromFsCache,
  navCategoriesDataFetcher,
} from '../../utils/fs-cache/nav-categories-cache';
import Footer from '../Footer';
import CoachingSessionAlertBanner from '../coaching-session/CoachingSessionAlertBanner';
import LoadingIndicator from '../common/LoadingIndicator';
import { LulNavbar } from '../navigation/LulNavbar';

import {
  type PageContentNavCategoryListingFragment,
  usePageContentHeaderDataQuery,
} from './__generated-gql-types__/PageContent.generated';
interface Props {
  hideSubnav?: boolean;
  isLoading?: boolean;
  hideFooter?: boolean;
  hideMessagesContainer?: boolean;
  hideNavBarButtons?: boolean;
  initialData: Possible<PageContentDataProps>;
  minimalNav?: boolean;
}

export interface PageContentDataProps {
  navCategoryListings: PageContentNavCategoryListingFragment[];
}

export const getStaticPageContentQuery =
  async (): Promise<PageContentDataProps> => {
    let navCategoryListings: PageContentNavCategoryListingFragment[];
    try {
      navCategoryListings = await getNavCategoriesFromFsCache();
    } catch {
      navCategoryListings = (await navCategoriesDataFetcher()).navCategories;
    }
    return { navCategoryListings };
  };

export const usePageContentDataQuery = (): PageContentDataProps => {
  const { data } = usePageContentHeaderDataQuery();

  return useMemo(
    () => ({
      navCategoryListings: data?.navCategories ?? [],
    }),
    [data],
  );
};

const PageContent: FC<PropsWithChildren<Props>> = ({
  hideSubnav,
  children = <></>,
  isLoading = false,
  hideFooter = false,
  initialData,
  minimalNav,
}) => {
  const [isNavigationSidebarOpen, setIsNavigationSidebarOpen] = useState(false);

  return (
    // @NOTE: flex-col-reverse to keep the header at the top of the stack context without z-index
    <div className="flex min-h-screen flex-col-reverse">
      {!hideFooter ? <Footer /> : null}
      <div className="flex grow flex-col">
        {isLoading ? (
          <LoadingIndicator iconClassName="w-20 h-20" className="grow" />
        ) : (
          <>
            <CoachingSessionAlertBanner />
            {children}
          </>
        )}
      </div>
      <button
        type="button"
        className={`fixed left-0 top-0 z-20 h-screen bg-black transition duration-200 ease-in-out lg:hidden ${
          isNavigationSidebarOpen ? 'w-full opacity-40' : 'opacity-0'
        }`}
        onClick={() => setIsNavigationSidebarOpen(false)}
      />
      <LulNavbar
        navCategories={initialData?.navCategoryListings}
        hideSubnav={hideSubnav}
        minimalNav={minimalNav}
      />
    </div>
  );
};

export default PageContent;
